import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Contexts
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Public Pages
import { Landing, About, Contact, Services, Pricing, Support, PrivacyPolicy, TermsOfService } from './pages/Public';
import ScheduleDemo from './pages/Public/ScheduleDemo';
import CookiePolicy from './pages/Public/CookiePolicy';

// Admin Pages
import AdminLogin from './pages/Admin/AdminLogin';
import AdminDashboard from './pages/Admin/Dashboard';
import AdminDemoRequests from './pages/Admin/AdminDemoRequests';
import AdminOrganizations from './pages/Admin/AdminOrganizations';
import AdminVendors from './pages/Admin/AdminVendors';
import AdminPlans from './pages/Admin/AdminPlans';
import AdminUsers from './pages/Admin/AdminUsers';
import AdminAnalytics from './pages/Admin/AdminAnalytics';
import AdminSettings from './pages/Admin/AdminSettings';
import AdminChat from './pages/Admin/AdminChat';
import AdminLayout from './components/Admin/AdminLayout';

// Pages
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import DemoRequests from './pages/DemoRequests';
import { Dashboard, ExecutiveDashboard, ProjectDashboard, TeamDashboard, FinancialDashboard } from './pages/Dashboard';
import { Projects, ProjectDetailPage, ProjectCreate, ProjectEdit } from './pages/Projects';
import { Portfolios, PortfolioCreate, PortfolioEdit } from './pages/Portfolios';
import Tasks from './pages/Tasks/Tasks';
import TaskDetail from './pages/Tasks/TaskDetail';
import TaskCreate from './pages/Tasks/TaskCreate';
import TaskEdit from './pages/Tasks/TaskEdit';
import TaskBoard from './pages/Tasks/TaskBoard';
import MilestoneListPage from './pages/Milestones/MilestoneListPage';
import MilestoneCreate from './pages/Milestones/MilestoneCreate';
import MilestoneEdit from './pages/Milestones/MilestoneEdit';
import { TimeTracking, TimeEntryCreate, TimeEntryEdit } from './pages/TimeTracking';
import { Reports, ReportCreate, ReportEdit, ReportView } from './pages/Reports';
import { Issues, IssueCreate, IssueEdit, IssueDetailPage } from './pages/Issues';
import { Budgets, BudgetCreate, BudgetEdit } from './pages/Budgets';
import { Expenses, ExpenseCreate, ExpenseEdit } from './pages/Expenses';
import Documents from './pages/Documents/Documents';
import DocumentCreate from './pages/Documents/DocumentCreate';
import DocumentEdit from './pages/Documents/DocumentEdit';
import DocumentDetail from './pages/Documents/DocumentDetail';
import Risks from './pages/Risks/Risks';
import RiskCreate from './pages/Risks/RiskCreate';
import RiskEdit from './pages/Risks/RiskEdit';
import RiskDetailPage from './pages/Risks/RiskDetailPage';
import { Forecasts, ForecastCreate, ForecastEdit, ForecastView } from './pages/Forecasts';
import Resources from './pages/Resources/Resources';
import ResourceAllocationCreate from './pages/Resources/ResourceAllocationCreate';
import ResourceAllocationEdit from './pages/Resources/ResourceAllocationEdit';
import Settings from './pages/Settings/Settings';
import RoleManagement from './pages/Settings/RoleManagement';
import RoleForm from './pages/Settings/RoleForm';
import Notifications from './pages/Notifications';
import ActivityLogs from './pages/ActivityLogs';
import Departments from './pages/Departments/Departments';
import DepartmentCreate from './pages/Departments/DepartmentCreate';
import DepartmentEdit from './pages/Departments/DepartmentEdit';
import DepartmentDetailPage from './pages/Departments/DepartmentDetailPage';
import { ChangeRequests, ChangeRequestCreate, ChangeRequestEdit, ChangeRequestView } from './pages/ChangeRequests';
import { Meetings, MeetingCreate, MeetingEdit, MeetingView } from './pages/Meetings';
import { Stakeholders, StakeholderCreate, StakeholderEdit, StakeholderView } from './pages/Stakeholders';
import { QualityMetrics, QualityMetricCreate, QualityMetricEdit, QualityMetricView } from './pages/QualityMetrics';
import { LessonsLearned, LessonLearnedCreate, LessonLearnedEdit, LessonLearnedView } from './pages/LessonsLearned';
import { Approvals, ApprovalCreate, ApprovalView } from './pages/Approvals';
import { AuditLogs, AuditLogView } from './pages/AuditLogs';
import { Templates, TemplateCreate, TemplateEdit, TemplateView } from './pages/Templates';
import { Integrations, IntegrationCreate, IntegrationEdit, IntegrationView } from './pages/Integrations';
import { Dependencies, DependencyCreate, DependencyEdit, DependencyView } from './pages/Dependencies';
import { Baselines, BaselineCreate, BaselineEdit, BaselineView } from './pages/Baselines';
import { Vendors, VendorOnboarding, VendorDetail } from './pages/Vendors';
import Chat from './pages/Chat/Chat';
import Todos from './pages/Todos';

// Vendor Portal Pages
import VendorLogin from './pages/VendorPortal/VendorLogin';
import VendorPortalDashboard from './pages/VendorPortal/VendorDashboard';
import VendorChat from './pages/VendorPortal/VendorChat';

// New Vendor Dashboard Pages
import { VendorDashboard, VendorTasks, VendorIssues, VendorCoVendors, VendorProfile } from './pages/Vendor';
import VendorLayout from './components/Vendor/VendorLayout';

// Components
import PrivateRoute from './components/PrivateRoute';
import AdminPrivateRoute from './components/AdminPrivateRoute';
import Layout from './components/Layout/Layout';

// Styles
import './App.css';
import AddUser from './components/UserOnboard';
import ErrorModal from './contexts/ErrorModal';
import VendorsProjectsList from './pages/Vendors/VendorProjects';
import VerifyEmail from './components/VerifyEmail';
import VerifyVendorEmail from './components/Vendor/VerifyVendorEmail';
import VendorContract from './pages/Vendors/UserVendorCreateContract';
import VendorContracts from './components/Vendor/VendorContracts';
import VendorContractList from './pages/Vendors/UserVendorContractList';
import UserVendorContractList from './pages/Vendors/UserVendorContractList';
import UserVendorCreateContract from './pages/Vendors/UserVendorCreateContract';
import UserVendorEditContract from './pages/Vendors/UserVendorEditContract';
import VendorForgotPassword from './pages/VendorPortal/VendorForgotPassword';
import UserForgotPassword from './pages/Auth/UserForogotPassword';
import VendorTimeTracking from './pages/Vendor/VendorTimeTracking';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <>
      <ErrorModal />
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider>
            <Router>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Landing />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/services" element={<Services />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/support" element={<Support />} />
                <Route path="/schedule-demo" element={<ScheduleDemo />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<UserForgotPassword />} />
                <Route path="/register" element={<Register />} />
                <Route path="/verify_mail" element={<VerifyEmail />} />

                {/* Vendor Portal Routes */}
                <Route path="/vendor/login" element={<VendorLogin />} />
                <Route path="/vendor/forgot-password" element={<VendorForgotPassword />} />
                {/* <Route path="/vendor/dashboard" element={<VendorDashboard />} /> */}
                <Route path="/vendor/chat/:conversationId" element={<VendorChat />} />
                <Route path="/vendor/verify_mail" element={<VerifyVendorEmail />} />

                {/* Vendor Dashboard with Layout */}
                <Route path="/vendor" element={<VendorLayout />}>
                  <Route index element={<Navigate to="/vendor/dashboard" replace />} />
                  <Route path="dashboard" element={<VendorDashboard />} />
                  <Route path="tasks" element={<VendorTasks />} />
                  <Route path="issues" element={<VendorIssues />} />
                  <Route path="co-vendors" element={<VendorCoVendors />} />
                  <Route path="projects" element={<VendorsProjectsList />} />
                  <Route path="portal" element={<VendorPortalDashboard />} />
                  <Route path="profile" element={<VendorProfile />} />
                  <Route path="contracts" element={<VendorContracts />} />
                  <Route path="time-tracking" element={<VendorTimeTracking />} />
                </Route>

                {/* Admin Routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin"
                  element={
                    <AdminPrivateRoute>
                      <AdminLayout />
                    </AdminPrivateRoute>
                  }
                >
                  <Route index element={<Navigate to="/admin/dashboard" replace />} />
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="demo-requests" element={<AdminDemoRequests />} />
                  <Route path="organizations" element={<AdminOrganizations />} />
                  <Route path="vendors" element={<AdminVendors />} />
                  <Route path="plans" element={<AdminPlans />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="analytics" element={<AdminAnalytics />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="chat" element={<AdminChat />} />
                  <Route path="profile" element={<div style={{ padding: '20px' }}>Profile Page - Coming Soon</div>} />
                  <Route path="add-user" element={<AddUser />} />

                </Route>

                {/* Protected Routes */}
                <Route
                  path="/app"
                  element={
                    <PrivateRoute>
                      <Layout />
                    </PrivateRoute>
                  }
                >
                  <Route index element={<Navigate to="/app/dashboard" replace />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="dashboard/executive" element={<ExecutiveDashboard />} />
                  <Route path="dashboard/projects" element={<ProjectDashboard />} />
                  <Route path="dashboard/team" element={<TeamDashboard />} />
                  <Route path="dashboard/financial" element={<FinancialDashboard />} />
                  <Route path="departments" element={<Departments />} />
                  <Route path="departments/new" element={<DepartmentCreate />} />
                  <Route path="departments/:id" element={<DepartmentDetailPage />} />
                  <Route path="departments/:id/edit" element={<DepartmentEdit />} />
                  <Route path="portfolios" element={<Portfolios />} />
                  <Route path="portfolios/new" element={<PortfolioCreate />} />
                  <Route path="portfolios/:id/edit" element={<PortfolioEdit />} />
                  <Route path="projects" element={<Projects />} />
                  <Route path="projects/new" element={<ProjectCreate />} />
                  <Route path="projects/:id" element={<ProjectDetailPage />} />
                  <Route path="projects/:id/edit" element={<ProjectEdit />} />
                  <Route path="tasks" element={<Tasks />} />
                  <Route path="tasks/board" element={<TaskBoard />} />
                  <Route path="tasks/new" element={<TaskCreate />} />
                  <Route path="tasks/:id" element={<TaskDetail />} />
                  <Route path="tasks/:id/edit" element={<TaskEdit />} />
                  <Route path="milestones" element={<MilestoneListPage />} />
                  <Route path="milestones/new" element={<MilestoneCreate />} />
                  <Route path="milestones/:id/edit" element={<MilestoneEdit />} />
                  <Route path="time-tracking" element={<TimeTracking />} />
                  <Route path="time-tracking/new" element={<TimeEntryCreate />} />
                  <Route path="time-tracking/:id/edit" element={<TimeEntryEdit />} />
                  <Route path="reports" element={<Reports />} />
                  <Route path="reports/new" element={<ReportCreate />} />
                  <Route path="reports/:id" element={<ReportView />} />
                  <Route path="reports/:id/edit" element={<ReportEdit />} />
                  <Route path="reports/:id/view" element={<ReportView />} />
                  <Route path="issues" element={<Issues />} />
                  <Route path="issues/create" element={<IssueCreate />} />
                  <Route path="issues/:id" element={<IssueDetailPage />} />
                  <Route path="issues/:id/edit" element={<IssueEdit />} />
                  <Route path="budgets" element={<Budgets />} />
                  <Route path="budgets/new" element={<BudgetCreate />} />
                  <Route path="budgets/:id/edit" element={<BudgetEdit />} />
                  <Route path="expenses" element={<Expenses />} />
                  <Route path="expenses/new" element={<ExpenseCreate />} />
                  <Route path="expenses/:id/edit" element={<ExpenseEdit />} />
                  <Route path="documents" element={<Documents />} />
                  <Route path="documents/upload" element={<DocumentCreate />} />
                  <Route path="documents/:id" element={<DocumentDetail />} />
                  <Route path="documents/:id/edit" element={<DocumentEdit />} />
                  <Route path="risks" element={<Risks />} />
                  <Route path="risks/new" element={<RiskCreate />} />
                  <Route path="risks/:id" element={<RiskDetailPage />} />
                  <Route path="risks/:id/edit" element={<RiskEdit />} />
                  <Route path="forecasts" element={<Forecasts />} />
                  <Route path="forecasts/new" element={<ForecastCreate />} />
                  <Route path="forecasts/:id" element={<ForecastView />} />
                  <Route path="forecasts/:id/edit" element={<ForecastEdit />} />
                  <Route path="resources" element={<Resources />} />
                  <Route path="resources/allocations/new" element={<ResourceAllocationCreate />} />
                  <Route path="resources/allocations/:id/edit" element={<ResourceAllocationEdit />} />
                  <Route path="change-requests" element={<ChangeRequests />} />
                  <Route path="change-requests/new" element={<ChangeRequestCreate />} />
                  <Route path="change-requests/:id" element={<ChangeRequestView />} />
                  <Route path="change-requests/:id/edit" element={<ChangeRequestEdit />} />
                  <Route path="meetings" element={<Meetings />} />
                  <Route path="meetings/new" element={<MeetingCreate />} />
                  <Route path="meetings/:id" element={<MeetingView />} />
                  <Route path="meetings/:id/edit" element={<MeetingEdit />} />
                  <Route path="stakeholders" element={<Stakeholders />} />
                  <Route path="stakeholders/new" element={<StakeholderCreate />} />
                  <Route path="stakeholders/:id" element={<StakeholderView />} />
                  <Route path="stakeholders/:id/edit" element={<StakeholderEdit />} />
                  <Route path="quality-metrics" element={<QualityMetrics />} />
                  <Route path="quality-metrics/new" element={<QualityMetricCreate />} />
                  <Route path="quality-metrics/:id" element={<QualityMetricView />} />
                  <Route path="quality-metrics/:id/edit" element={<QualityMetricEdit />} />
                  <Route path="lessons-learned" element={<LessonsLearned />} />
                  <Route path="lessons-learned/new" element={<LessonLearnedCreate />} />
                  <Route path="lessons-learned/:id" element={<LessonLearnedView />} />
                  <Route path="lessons-learned/:id/edit" element={<LessonLearnedEdit />} />
                  <Route path="approvals" element={<Approvals />} />
                  <Route path="approvals/new" element={<ApprovalCreate />} />
                  <Route path="approvals/:id" element={<ApprovalView />} />
                  <Route path="audit-logs" element={<AuditLogs />} />
                  <Route path="audit-logs/:id" element={<AuditLogView />} />
                  <Route path="templates" element={<Templates />} />
                  <Route path="templates/new" element={<TemplateCreate />} />
                  <Route path="templates/:id" element={<TemplateView />} />
                  <Route path="templates/:id/edit" element={<TemplateEdit />} />
                  <Route path="integrations" element={<Integrations />} />
                  <Route path="integrations/new" element={<IntegrationCreate />} />
                  <Route path="integrations/:id" element={<IntegrationView />} />
                  <Route path="integrations/:id/edit" element={<IntegrationEdit />} />
                  <Route path="dependencies" element={<Dependencies />} />
                  <Route path="dependencies/new" element={<DependencyCreate />} />
                  <Route path="dependencies/:id" element={<DependencyView />} />
                  <Route path="dependencies/:id/edit" element={<DependencyEdit />} />
                  <Route path="baselines" element={<Baselines />} />
                  <Route path="baselines/create" element={<BaselineCreate />} />
                  <Route path="baselines/:id" element={<BaselineView />} />
                  <Route path="baselines/:id/edit" element={<BaselineEdit />} />
                  <Route path="vendors" element={<Vendors />} />
                  <Route path="vendors/create" element={<VendorOnboarding />} />
                  <Route path="vendors/:id" element={<VendorDetail />} />
                  <Route path="vendors/:id/edit" element={<VendorOnboarding />} />
                  <Route path="todos" element={<Todos />} />
                  <Route path="chat" element={<Chat />} />
                  <Route path="demo-requests" element={<DemoRequests />} />
                  <Route path="notifications" element={<Notifications />} />
                  <Route path="activity-logs" element={<ActivityLogs />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="settings/roles" element={<RoleManagement />} />
                  <Route path="settings/roles/create" element={<RoleForm />} />
                  <Route path="settings/roles/:id/edit" element={<RoleForm />} />
                  <Route path="vendors/:id/contracts" element={<UserVendorContractList />} />
                  <Route path="vendors/:id/create-contract" element={<UserVendorCreateContract />} />
                  <Route path="vendors/:id/contract-edit" element={<UserVendorEditContract />} />
                </Route>

                {/* 404 */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Router>
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </>
  );
}

export default App;


